import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../../api';

// Department x Client-Classification (New/Existing/Priority) project count matrix.
// Classification reuses the existing Tagging system (see backend's
// ClientClassificationTagSeeder) — a company is "New Client"/"Existing Client"/"Priority"
// by having that tag assigned via the Client page's existing Tagging picker.
const ClientProjectMatrix = () => {
  const navigate = useNavigate();
  const [tagColumns, setTagColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/crm/sales/client-monitoring/matrix');
      setTagColumns(res.data?.tagColumns || []);
      setRows(res.data?.rows || []);
    } catch (err) {
      setError('Gagal memuat data Project Matrix.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columnTotal = (tag) => rows.reduce((sum, row) => sum + (row.counts?.[tag] || 0), 0);
  const grandTotal = rows.reduce((sum, row) => sum + (row.total || 0), 0);

  const goToClients = (departmentId, tag) => {
    const params = new URLSearchParams();
    if (departmentId != null) params.set('department', String(departmentId));
    navigate(`/audit/clients?${params.toString()}`);
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-2 d-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34, background: 'rgba(31,101,108,0.10)' }}>
              <LayoutGrid size={18} color="#1f656c" />
            </div>
            <h1 className="h4 mb-0 fw-semibold" style={{ color: '#1a2e35' }}>Client Project Matrix</h1>
          </div>
          <div className="text-muted small mt-1">
            Jumlah project per Departemen, disilangkan dengan klasifikasi client (New / Existing / Priority).
          </div>
        </div>
        <button type="button" className="btn btn-outline-secondary d-flex align-items-center gap-1" onClick={load}>
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3">
          <AlertTriangle size={16} /> <span className="small">{error}</span>
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {rows.length === 0 && !loading && !error && (
            <div className="text-muted small text-center py-4">
              Belum ada data. Pastikan lead memiliki Departemen dan company sudah di-tag New Client/Existing Client/Priority di halaman Client.
            </div>
          )}
          {(loading || rows.length > 0) && (
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Departemen</th>
                    {tagColumns.map((tag) => <th key={tag} className="text-center">{tag}</th>)}
                    <th className="text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={tagColumns.length + 2} className="text-center text-muted py-4">Memuat...</td></tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.departmentId ?? 'none'}>
                        <td className="fw-medium">{row.departmentName || 'Tanpa Departemen'}</td>
                        {tagColumns.map((tag) => (
                          <td
                            key={tag}
                            className="text-center"
                            role="button"
                            style={{ cursor: 'pointer' }}
                            onClick={() => goToClients(row.departmentId, tag)}
                          >
                            {row.counts?.[tag] || 0}
                          </td>
                        ))}
                        <td className="text-center fw-bold">{row.total || 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!loading && rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="fw-bold">Total</td>
                      {tagColumns.map((tag) => <td key={tag} className="text-center fw-bold">{columnTotal(tag)}</td>)}
                      <td className="text-center fw-bold">{grandTotal}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientProjectMatrix;
